import { useEffect, useRef, useState } from 'react';
import { MdCheckCircle, MdErrorOutline } from 'react-icons/md';
import { api, ApiError } from '@podus/podus-api';

interface Completed {
    googleEmail: string;
    stateName: string;
    returnUrl: string;
}

type Outcome =
    | { kind: 'working' }
    | { kind: 'connected'; result: Completed }
    | { kind: 'failed'; message: string; stateName?: string; returnUrl?: string };

/** Only http(s) addresses get a button; the address comes from Podus, but check anyway. */
function safeUrl(value: string | undefined, connected: boolean): string | null {
    if (!value) return null;
    try {
        const url = new URL(value);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
        if (connected) url.searchParams.set('google', 'connected');
        return url.toString();
    } catch {
        return null;
    }
}

/**
 * `podus.org/multidoor/google-connected`: Google sends every Multi-Door Court's Admin here after they approve
 * (or refuse) the court's Google account. Podus finishes the connection from the code and the signed state, and
 * the page links back to that court's Settings page. Nobody needs to be signed in here.
 */
export default function MultidoorGoogleConnected() {
    const [outcome, setOutcome] = useState<Outcome>({ kind: 'working' });
    // React Strict Mode runs effects twice in development; Google's code works only once.
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return;
        started.current = true;
        document.title = 'Connecting Google | Podus Multidoor';

        const params = new URLSearchParams(window.location.search);
        const state = params.get('state');
        const code = params.get('code');
        const error = params.get('error');
        // Keep the one-time code out of the browser history.
        window.history.replaceState(null, '', window.location.pathname);

        if (!state || (!code && !error)) {
            setOutcome({
                kind: 'failed',
                message:
                    'Google did not send back what is needed to connect the account. Go back to Settings in your Multidoor dashboard and connect again.',
            });
            return;
        }

        api.post<{ data: Completed }>('/multidoor/google/complete', code ? { state, code } : { state, error }, {
            anonymous: true,
        })
            .then((res) => setOutcome({ kind: 'connected', result: res.data }))
            .catch((err: unknown) => {
                const back = (err instanceof ApiError ? err.payload : null) as {
                    response?: { data?: { stateName?: string; returnUrl?: string } };
                } | null;
                // Only the route's own answers (400) are written for the Admin. Anything else means Podus couldn't
                // be reached or doesn't have the route (an API older than this page), so keep the detail for the console.
                const ours = err instanceof ApiError && err.status === 400;
                if (!ours) console.error('[multidoor] Finishing the Google connection failed:', err);
                setOutcome({
                    kind: 'failed',
                    message: ours
                        ? err.message
                        : "Podus couldn't finish connecting the court's Google account just now. Go back to Settings in your Multidoor dashboard and try again.",
                    stateName: back?.response?.data?.stateName,
                    returnUrl: back?.response?.data?.returnUrl,
                });
            });
    }, []);

    const connected = outcome.kind === 'connected';
    const stateName = connected ? outcome.result.stateName : outcome.kind === 'failed' ? outcome.stateName : undefined;
    const backUrl = safeUrl(
        connected ? outcome.result.returnUrl : outcome.kind === 'failed' ? outcome.returnUrl : undefined,
        connected,
    );

    useEffect(() => {
        if (outcome.kind === 'connected') document.title = 'Google account connected | Podus Multidoor';
        if (outcome.kind === 'failed') document.title = 'Google account not connected | Podus Multidoor';
    }, [outcome.kind]);

    return (
        // Top-aligned on phones so the button stays clear of the cookie banner at the bottom.
        <main className="flex min-h-dvh items-start justify-center bg-background px-4 pt-12 pb-10 font-primary sm:items-center sm:py-10">
            <div className="w-full max-w-md text-center">
                <img src="/logo/podus-multidoor.svg" alt="Podus Multidoor" className="mx-auto h-14 w-auto" />

                <div className="mt-8 rounded-2xl border border-[#e5e5e5] bg-white px-6 py-10 shadow-sm sm:px-10 dark:border-[#2a2a2a] dark:bg-[#1e1e1e]">
                    {outcome.kind === 'working' && (
                        <div role="status">
                            <span className="mx-auto block h-10 w-10 animate-spin rounded-full border-4 border-[#eee] border-t-primary" />
                            <p className="mt-6 text-[15px] text-secondary-300 dark:text-dark-text-secondary">
                                Connecting the court's Google account…
                            </p>
                        </div>
                    )}

                    {outcome.kind !== 'working' && (
                        <>
                            {connected ? (
                                <MdCheckCircle className="mx-auto text-6xl text-green-600" aria-hidden />
                            ) : (
                                <MdErrorOutline className="mx-auto text-6xl text-primary" aria-hidden />
                            )}
                            <h1 className="mt-5 text-2xl font-bold text-secondary dark:text-dark-text">
                                {connected ? 'Google account connected' : 'Google account not connected'}
                            </h1>
                            <p className="mt-3 text-[15px] leading-relaxed text-secondary-300 dark:text-dark-text-secondary">
                                {connected ? (
                                    <>
                                        Online sessions at the {outcome.result.stateName} Multi-Door Court will get a
                                        Google Meet link from
                                        <span className="mt-1 block font-semibold break-words text-secondary dark:text-dark-text">
                                            {outcome.result.googleEmail}
                                        </span>
                                    </>
                                ) : (
                                    outcome.message
                                )}
                            </p>

                            {backUrl ? (
                                <a
                                    href={backUrl}
                                    className="mt-8 block w-full rounded-xl bg-primary px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-primary/90"
                                >
                                    Back to {stateName ? `${stateName} ` : ''}Multidoor
                                </a>
                            ) : (
                                <p className="mt-8 text-[13px] text-secondary-200 dark:text-dark-text-secondary">
                                    You can close this page.
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
